import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { dailyNormCard } from '@/styles/tokens';

interface DailyNormCardProps {
  icon: React.ReactElement;
  label: string;
  value: number | string | null | undefined;
  unit: string;
  /** Дія олівця; без неї олівець лише відображається. */
  onEdit?: () => void;
}

// Картка денної норми (Figma): іконка + підпис, значення + одиниця, олівець у правому верхньому куті.
export const DailyNormCard: React.FC<DailyNormCardProps> = ({ icon, label, value, unit, onEdit }) => {
  const { t } = useTranslation();
  return (
  <Box
    sx={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '12px',
      // Рамка завжди є (прозора), щоб hover-стан не зсував вміст.
      p: '19px',
      border: '1px solid transparent',
      borderRadius: '12px',
      bgcolor: dailyNormCard.background,
      boxShadow: dailyNormCard.shadow,
      minWidth: 0,
      '&:hover': {
        bgcolor: dailyNormCard.backgroundHover,
        borderColor: dailyNormCard.borderHover,
        boxShadow: dailyNormCard.shadowHover,
      },
      '&:hover .daily-norm-edit': {
        bgcolor: dailyNormCard.editBgHover,
        color: dailyNormCard.editHover,
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: 0, pr: 3 }}>
      {React.cloneElement(icon, { sx: { fontSize: 16, color: dailyNormCard.icon } })}
      <Typography noWrap sx={{ fontSize: 14, lineHeight: '16px', color: dailyNormCard.textMuted }}>
        {label}
      </Typography>
    </Box>

    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
      <Typography sx={{ fontSize: 16, lineHeight: '19px', fontWeight: 600, color: dailyNormCard.text }}>
        {value ?? '—'}
      </Typography>
      {value != null && (
        <Typography sx={{ fontSize: 16, lineHeight: '19px', color: dailyNormCard.textMuted }}>{unit}</Typography>
      )}
    </Box>

    <IconButton
      className="daily-norm-edit"
      aria-label={t('clientProfile.dailyNorms.editAria', { label })}
      onClick={onEdit}
      disableRipple={!onEdit}
      sx={{
        position: 'absolute',
        top: 11,
        right: 11,
        width: 24,
        height: 24,
        p: '4px',
        borderRadius: '4px',
        color: dailyNormCard.edit,
        cursor: onEdit ? 'pointer' : 'default',
        '&:hover': { bgcolor: dailyNormCard.editBgHover },
      }}
    >
      <EditIcon sx={{ fontSize: 16 }} />
    </IconButton>
  </Box>
);
};
